import { PdmFailedDelivery } from '@/api/receive';
import { FC, memo } from 'react';
import styled from 'styled-components';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@material-ui/core';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import WarningIcon from '@material-ui/icons/Warning';
import CloseIcon from '@material-ui/icons/Close';

import { UIButton } from '@/components/ui/Button';
import { colors } from '@/theme';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface PdmResultModalProps {
  open: boolean;
  title: string;
  iconType?: 'WARNING' | 'ERROR';
  closedDeliveries: string[];
  failedDeliveries: PdmFailedDelivery[];
  onClose: () => void;
}

// ─── Styled Components ────────────────────────────────────────────────────────

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    width: 640px;
    max-width: 640px;
    max-height: 80vh;
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
`;

const StyledDialogTitle = styled(DialogTitle)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
  border-bottom: 1px solid ${colors.lightGrey};
  flex-shrink: 0;
`;

const StyledDialogContent = styled(DialogContent)`
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${colors.lightGrey};
    border-radius: 4px;
  }
`;

const StyledDialogActions = styled(DialogActions)`
  padding: 14px 24px;
  border-top: 1px solid ${colors.lightGrey};
  flex-shrink: 0;
`;

const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;

  span {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${colors.grey};
  }
`;

const DeliveryRow = styled.div<{ variant: 'success' | 'error' }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 8px 12px;
  border-radius: 6px;
  background-color: ${({ variant }) =>
    variant === 'success' ? `${colors.green}18` : `${colors.red}18`};

  & + & {
    margin-top: 4px;
  }
`;

const DeliveryNumber = styled.span`
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  font-size: 14px;
`;

const DeliveryReason = styled.span`
  font-size: 13px;
  text-align: right;
  color: ${colors.grey};
`;

// ─── Component ────────────────────────────────────────────────────────────────

export const PdmResultModal: FC<PdmResultModalProps> = memo(
  ({ open, title, iconType, closedDeliveries, failedDeliveries, onClose }) => {
    return (
      <StyledDialog open={open} onClose={onClose} maxWidth={false}>
        <StyledDialogTitle disableTypography>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {iconType === 'ERROR' && (
              <ErrorOutlineIcon
                style={{ color: colors.red, width: 22, height: 22 }}
              />
            )}
            {iconType === 'WARNING' && (
              <WarningIcon
                style={{ color: colors.yellow, width: 22, height: 22 }}
              />
            )}
            <span style={{ fontSize: 16, fontWeight: 600, color: colors.text }}>
              {title}
            </span>
          </div>
          <IconButton size="small" onClick={onClose} aria-label="chiudi">
            <CloseIcon style={{ width: 18, height: 18 }} />
          </IconButton>
        </StyledDialogTitle>

        <StyledDialogContent>
          {closedDeliveries.length > 0 && (
            <div>
              <SectionLabel>
                <CheckCircleOutlineIcon
                  style={{ width: 14, height: 14, color: colors.green }}
                />
                <span>Deliveries closed</span>
              </SectionLabel>
              {closedDeliveries.map(dn => (
                <DeliveryRow key={dn} variant="success">
                  <DeliveryNumber style={{ color: colors.green }}>
                    {dn}
                  </DeliveryNumber>
                </DeliveryRow>
              ))}
            </div>
          )}

          {failedDeliveries.length > 0 && (
            <div>
              <SectionLabel>
                <ErrorOutlineIcon
                  style={{ width: 14, height: 14, color: colors.red }}
                />
                <span>Deliveries failed</span>
              </SectionLabel>
              {failedDeliveries.map(fd => (
                <DeliveryRow key={fd.deliveryNumber} variant="error">
                  <DeliveryNumber style={{ color: colors.red }}>
                    {fd.deliveryNumber}
                  </DeliveryNumber>
                  <DeliveryReason>{fd.reason}</DeliveryReason>
                </DeliveryRow>
              ))}
            </div>
          )}
        </StyledDialogContent>

        <StyledDialogActions>
          <UIButton outlined label="Close" onClick={onClose} />
        </StyledDialogActions>
      </StyledDialog>
    );
  }
);

PdmResultModal.displayName = 'PdmResultModal';
export default PdmResultModal;
